import type { PresentationStyle } from 'paintscout'
import { getDefaultPresentationFontFamilies } from './loadPresentationFonts'

/**
 * `default` and `alt` name one of the style's two global faces rather than a face of their own, so
 * they are already covered by `fontFamily`/`altFontFamily`. Georgia and Roboto are served as system
 * faces (see `CURATED_FONT_OPTIONS`), so asking Google for them is a wasted round trip.
 */
const NON_FETCHABLE_STYLE_FONTS = ['default', 'alt', 'georgia, serif', 'roboto, sans-serif']

const TYPOGRAPHY_SLOTS = ['body', 'heading1', 'heading2', 'heading3'] as const

/** The families a style actually names, in the order the renderer needs them, deduped. */
export function getPresentationStyleFontFamilies(style?: PresentationStyle): string[] {
  if (!style) return []

  const fonts: string[] = []
  const push = (family?: string) => {
    if (family && !fonts.includes(family)) fonts.push(family)
  }

  push(style.fontFamily)
  push(style.altFontFamily)
  TYPOGRAPHY_SLOTS.forEach((slot) => push(style[slot]?.fontFamily))

  return fonts.filter((font) => !NON_FETCHABLE_STYLE_FONTS.includes(font))
}

/**
 * The font families a presentation render should ask `loadPresentationFonts` for.
 *
 * `loadAllFonts` is ADDITIVE, not a replacement, and it is expensive: the default set is every
 * prefab style's font plus all of `CURATED_FONT_OPTIONS`, each requested at six variants. Only a
 * surface that swaps between several styles in one preview should pay for it — currently just
 * `NewPresentationDialog`. Every other preview loads what the customer view loads, which is the
 * only set that can be correct for a preview.
 */
export function getPresentationFontFamilies(args: { style?: PresentationStyle; loadAllFonts?: boolean }): string[] {
  const { style, loadAllFonts } = args
  const styleFonts = getPresentationStyleFontFamilies(style)

  return loadAllFonts ? [...getDefaultPresentationFontFamilies(), ...styleFonts] : styleFonts
}
